import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type IpWhoisData = {
  ip?: string;
  success?: boolean;
  type?: string;
  continent?: string;
  continent_code?: string;
  country?: string;
  country_code?: string;
  region?: string;
  region_code?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  is_eu?: boolean;
  postal?: string;
  calling_code?: string;
  capital?: string;
  borders?: string;
  flag?: { img?: string; emoji?: string; emoji_unicode?: string };
  connection?: { asn?: number | string; org?: string; isp?: string; domain?: string };
  timezone?: { id?: string; abbr?: string; is_dst?: boolean; offset?: number; utc?: string; current_time?: string };
};

function getPayload(results: any[]): { domain?: string; ip?: string; ipwhoisData?: IpWhoisData; message?: string } | null {
  if (!Array.isArray(results) || results.length === 0) return null;
  const first = results[0];
  return first && typeof first === 'object' ? first : null;
}

const IpWhoisResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const data = getPayload(results);
  const info = data?.ipwhoisData;

  // Si no llega objeto válido o el worker marcó fallo, mostramos vacío
  const noData = !data || !info || info.success === false;

  if (noData) {
    return (
      <p className="text-sm text-slate-500">
        {t('tools.results_scan.ipwhois.empty')}
      </p>
    );
  }

  const domain = data?.domain || '-';
  const ip = data?.ip || info?.ip || '-';
  const flagEmoji = info?.flag?.emoji || '';
  const flagImg = info?.flag?.img;

  return (
    <div className="w-full space-y-4">
      {/* Cabecera centrada: Dominio → IP */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex items-center gap-2">
          {flagImg ? (
            <img
              src={flagImg}
              alt={info?.country || 'flag'}
              className="h-5 w-7 rounded-sm ring-1 ring-slate-200"
            />
          ) : flagEmoji ? (
            <span className="text-xl leading-none">{flagEmoji}</span>
          ) : null}
          <span className="text-sm text-slate-600">
            {t('tools.results_scan.ipwhois.resolved_for')}
          </span>
        </div>
        <div className="text-base md:text-lg font-semibold text-slate-900">
          {domain} <span className="mx-2 text-slate-400">→</span> {ip}
        </div>
        {(info?.city || info?.country) && (
          <div className="text-xs text-slate-500">
            {[info.city, info.country].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Grid 3 columnas */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Ubicación */}
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-gradient-to-r from-sky-50 to-indigo-50 text-slate-700">
            {t('tools.results_scan.ipwhois.location')}
          </div>
          <div className="p-3 text-sm">
            <dl className="space-y-1">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.continent')}</dt>
                <dd className="text-slate-900">{info?.continent || '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.country')}</dt>
                <dd className="text-slate-900">{info?.country || '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.region')}</dt>
                <dd className="text-slate-900">{info?.region || '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.city')}</dt>
                <dd className="text-slate-900">{info?.city || '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.postal')}</dt>
                <dd className="text-slate-900">{info?.postal || '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Lat / Lon</dt>
                <dd className="text-slate-900">
                  {info?.latitude != null && info?.longitude != null ? `${info.latitude}, ${info.longitude}` : '-'}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.is_eu')}</dt>
                <dd className="text-slate-900">{info?.is_eu ? t('tools.results_scan.ipwhois.yes') : t('tools.results_scan.ipwhois.no')}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Conexión */}
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-gradient-to-r from-sky-50 to-indigo-50 text-slate-700">
            {t('tools.results_scan.ipwhois.connection')}
          </div>
          <div className="p-3 text-sm">
            <dl className="space-y-1">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">ASN</dt>
                <dd className="text-slate-900">{info?.connection?.asn ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.org')}</dt>
                <dd className="text-slate-900">{info?.connection?.org ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">ISP</dt>
                <dd className="text-slate-900">{info?.connection?.isp ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Domain</dt>
                <dd className="text-slate-900">{info?.connection?.domain ?? '-'}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Zona horaria */}
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-gradient-to-r from-sky-50 to-indigo-50 text-slate-700">
            {t('tools.results_scan.ipwhois.timezone')}
          </div>
          <div className="p-3 text-sm">
            <dl className="space-y-1">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">ID</dt>
                <dd className="text-slate-900">{info?.timezone?.id ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.abbr')}</dt>
                <dd className="text-slate-900">{info?.timezone?.abbr ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">UTC</dt>
                <dd className="text-slate-900">{info?.timezone?.utc ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{t('tools.results_scan.ipwhois.current_time')}</dt>
                <dd className="text-slate-900">{info?.timezone?.current_time ?? '-'}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IpWhoisResults;
